import { inject } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	ResolveFn,
	RouterStateSnapshot
} from '@angular/router';
import { Store } from '@ngrx/store';
import { currentApp } from '@store/reducers/app.reducer';
import { filter, Observable, of, switchMap, take } from 'rxjs';
import { WidgetConfig } from '../../../definitions/app-structure';
import { WidgetsService } from '@widgets-framework/services/widgets.service';

/**
 * Resolves a WidgetConfig entity from the route parameters.
 *
 * @remarks
 * On the flat `apps/:appId/widgets/:widgetId` route this resolver runs in
 * parallel with `AppResolverFn`, which populates `currentApp` in the store.
 * `WidgetsService.find` builds the app-scoped URL
 * `applications/{appId}/widgets/{id}` from `currentApp`, so calling it before
 * the app is loaded throws `Current app not selected` and cancels the in-flight
 * application request on a cold load / refresh. We therefore wait until the
 * current application matching the route `appId` is available before requesting
 * the widget.
 */
export const widgetResolverFn: ResolveFn<WidgetConfig> = (
	route: ActivatedRouteSnapshot,
	state: RouterStateSnapshot
): Observable<WidgetConfig> => {
	const widgetSvc = inject(WidgetsService);
	const store = inject(Store);

	const appId = Number(route.paramMap.get('appId'));
	const widgetId = route.paramMap.get('widgetId');

	if (!widgetId) {
		return of(null);
	}

	return store.select(currentApp).pipe(
		filter((app) => app?.id === appId),
		take(1),
		switchMap(() => widgetSvc.find(+widgetId))
	);
};
